import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Drawer, List, ListItem, ListItemIcon, ListItemButton, Box,
  Tooltip, Typography, IconButton, Avatar, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Backdrop, CircularProgress,
} from '@mui/material';
import { Close, PowerSettingsNew } from '@mui/icons-material';

import sidebarIcon  from '../../assets/amzyticks_icon_128.png';
import amzytickLogo from '../../assets/amzyticks_logo.png';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';

import { logout } from '../../store/authSlice';
import { clearOrder } from '../../store/orderSlice';
import { useLogoutApiMutation } from '../../services/authApi';
import { useToast } from './ToastProvider';

const menuItems = [
  { text: 'Dashboard',     icon: <DashboardCustomizeOutlinedIcon />, route: '/dashboard' },
  { text: 'Order History', icon: <HistoryOutlinedIcon />,            route: '/orders' },
];
const LOGOUT_TRANSITION_MS = 1200;

function useLogout() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [logoutApi] = useLogoutApiMutation();
  const showToast = useToast();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try { await logoutApi().unwrap(); } catch (_) {}

    showToast('Logout successful', 'success');

    setTimeout(() => {
      dispatch(clearOrder());
      dispatch(logout());
      navigate('/login', { replace: true });
    }, LOGOUT_TRANSITION_MS);
  };

  return { handleLogout, isLoggingOut };
}

function LogoutTransitionBackdrop({ open }) {
  return (
    <Backdrop
      open={open}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 10,
        bgcolor: 'rgba(15, 23, 42, 0.72)',
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <img src={amzytickLogo} alt="Amzyticks" style={{ width: 160, height: 'auto', display: 'block' }} />
        </Box>
        <CircularProgress size={28} sx={{ color: '#fff', mb: 1.5 }} />
        <Typography sx={{ color: '#fff', fontSize: '0.95rem', fontWeight: 700 }}>
          Logging you out...
        </Typography>
      </Box>
    </Backdrop>
  );
}

// ─── Shared logout confirmation dialog ───────────────────────────────────────

function LogoutDialog({ open, onClose, onConfirm }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: '12px', p: 0.5 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <PowerSettingsNew sx={{ fontSize: 20, color: '#EF4444' }} />
        </Box>
        <Typography fontWeight={800} fontSize="1rem" color="#1E293B">End Session</Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 0, pb: 1 }}>
        <Typography fontSize="0.875rem" color="#64748B">
          Are you sure you want to logout from the current session?
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" fullWidth
          sx={{ borderRadius: '8px', fontWeight: 700, borderColor: '#E2E8F0', color: '#64748B', '&:hover': { bgcolor: '#F8FAFC', borderColor: '#CBD5E1' } }}
        >
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" fullWidth disableElevation
          sx={{ borderRadius: '8px', fontWeight: 700, bgcolor: '#EF4444', '&:hover': { bgcolor: '#DC2626' } }}
        >
          Logout
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Desktop: permanent icon-only sidebar ───────────────────────────────────

function DesktopSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleLogout, isLoggingOut } = useLogout();
  const user = useSelector((state) => state.auth.user);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const initials = user?.name ? user.name.charAt(0).toUpperCase() : 'C';

  return (
    <>
      <Drawer
        variant="permanent"
        PaperProps={{
          sx: {
            width: 70,
            position: 'fixed',
            minHeight: '100vh',
            height: '100dvh',
            bgcolor: '#fff',
            borderRight: '1px solid #e0e0e0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            overflow: 'hidden',
          },
        }}
      >
        {/* Logo — square icon mark, fits perfectly in 70px sidebar */}
        <Box sx={{ py: 2.5, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
          <img src={sidebarIcon} alt="Amzyticks" style={{ width: 36, height: 36, objectFit: 'contain', display: 'block' }} />
        </Box>

        {/* Nav items */}
        <List sx={{ width: '100%', pt: 0, flexGrow: 1 }}>
          {menuItems.map((item) => {
            const isSelected = location.pathname.startsWith(item.route);
            return (
              <Tooltip key={item.text} title={item.text} placement="right" arrow disableInteractive>
                <ListItem
                  onClick={() => navigate(item.route)}
                  sx={{
                    justifyContent: 'center',
                    px: 0,
                    py: 1.5,
                    cursor: 'pointer',
                    borderLeft: isSelected ? '4px solid #00acc1' : '4px solid transparent',
                    '&:hover': { bgcolor: '#f8f9fa' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 0, color: isSelected ? '#00acc1' : '#757575' }}>
                    {React.cloneElement(item.icon, { sx: { fontSize: 26 } })}
                  </ListItemIcon>
                </ListItem>
              </Tooltip>
            );
          })}
        </List>

        {/* User initial + power button */}
        <Box sx={{ pb: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Divider sx={{ width: 40, mb: 0.5 }} />
          <Tooltip title={user?.name || 'Cashier'} placement="right" arrow disableInteractive>
            <Avatar sx={{ width: 32, height: 32, bgcolor: '#EEF2FF', color: '#4F46E5', fontSize: '0.8rem', fontWeight: 800 }}>
              {initials}
            </Avatar>
          </Tooltip>
          <Tooltip title="Logout" placement="right" arrow disableInteractive>
            <IconButton
              onClick={() => setConfirmOpen(true)}
              sx={{
                width: 36, height: 36,
                color: '#EF4444',
                bgcolor: '#FEF2F2',
                borderRadius: '10px',
                '&:hover': { bgcolor: '#FEE2E2' },
              }}
            >
              <PowerSettingsNew sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Drawer>

      <LogoutDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          handleLogout();
        }}
      />
      <LogoutTransitionBackdrop open={isLoggingOut} />
    </>
  );
}

// ─── Mobile: temporary drawer with icon + label ──────────────────────────────

function MobileSidebar({ open, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleLogout, isLoggingOut } = useLogout();
  const user = useSelector((state) => state.auth.user);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const initials = user?.name ? user.name.charAt(0).toUpperCase() : 'C';

  const handleNavClick = (route) => {
    navigate(route);
    onClose();
  };

  return (
    <>
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: 272,
            bgcolor: '#fff',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid #F1F5F9', flexShrink: 0 }}>
          <img src={amzytickLogo} alt="Amzyticks" style={{ width: 110, height: 'auto', display: 'block', flexShrink: 0 }} />
          <Box sx={{ flexGrow: 1 }} />
          <IconButton size="small" onClick={onClose} sx={{ color: '#94A3B8' }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        {/* Nav list */}
        <Box sx={{
          flexGrow: 1, overflowY: 'auto',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: '10px' },
        }}>
          <List disablePadding sx={{ pt: 1 }}>
            {menuItems.map((item) => {
              const isSelected = location.pathname.startsWith(item.route);
              return (
                <ListItemButton
                  key={item.text}
                  onClick={() => handleNavClick(item.route)}
                  sx={{
                    px: 2.5, py: 1.25,
                    borderLeft: isSelected ? '3px solid #00acc1' : '3px solid transparent',
                    bgcolor: isSelected ? '#F0FAFA' : 'transparent',
                    '&:hover': { bgcolor: '#F8FAFC' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: isSelected ? '#00acc1' : '#64748B' }}>
                    {React.cloneElement(item.icon, { sx: { fontSize: 22 } })}
                  </ListItemIcon>
                  <Typography sx={{
                    fontSize: '0.875rem', fontWeight: isSelected ? 700 : 600,
                    color: isSelected ? '#00acc1' : '#374151',
                  }}>
                    {item.text}
                  </Typography>
                </ListItemButton>
              );
            })}
          </List>
        </Box>

        {/* User + Power button footer */}
        <Box sx={{ borderTop: '1px solid #F1F5F9', flexShrink: 0 }}>
          <Box sx={{ px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: '#4F46E5', fontSize: '0.85rem', fontWeight: 800 }}>
              {initials}
            </Avatar>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography fontSize="0.82rem" fontWeight={700} color="#1E293B" noWrap>
                {user?.name || 'Cashier'}
              </Typography>
              <Typography fontSize="0.68rem" color="#94A3B8" fontWeight={600}>
                Cashier Terminal
              </Typography>
            </Box>
            <Tooltip title="Logout" placement="top" arrow>
              <IconButton
                size="small"
                onClick={() => setConfirmOpen(true)}
                sx={{
                  color: '#EF4444',
                  bgcolor: '#FEF2F2',
                  borderRadius: '8px',
                  '&:hover': { bgcolor: '#FEE2E2' },
                }}
              >
                <PowerSettingsNew fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Drawer>

      <LogoutDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          onClose();
          handleLogout();
        }}
      />
      <LogoutTransitionBackdrop open={isLoggingOut} />
    </>
  );
}

// ─── Main export: auto-switches based on breakpoint ─────────────────────────

function Sidebar({ mobileOpen, onMobileClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (isMobile) {
    return <MobileSidebar open={mobileOpen} onClose={onMobileClose} />;
  }

  return <DesktopSidebar />;
}

export default Sidebar;
